import { User } from "lucide-react";
import { EntityLink, EntityLinkPlain } from "./EntityLink";

/**
 * ChildLink — canonical "First Last (internalId)" rendering, e.g.
 * "Katarina Purnima (0451)", linking to the child detail page.
 *
 * Accepts either a pre-combined `name` or separate `firstName`/`lastName`.
 * The `(internalId)` suffix is shown whenever `childInternalId` is present
 * (set `showId={false}` to suppress, e.g. when already on the child's page).
 * Falls back to plain text when `childId` is missing. `search` opens a tab of
 * the child page, e.g. `tab=portfolio`.
 */
export interface ChildLinkProps {
  childId?: string | null;
  childInternalId?: string | null;
  /** Full name if already combined upstream (e.g. invoice.childName). */
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  showId?: boolean;
  /** Query string without the `?`, e.g. `tab=portfolio`. */
  search?: string;
  className?: string;
  "data-testid"?: string;
}

function formatChildLabel({
  childInternalId,
  name,
  firstName,
  lastName,
  showId = true,
}: ChildLinkProps): string {
  const fullName = (name ?? [firstName, lastName].filter(Boolean).join(" ")).trim();
  const base = fullName || "—";
  if (showId && childInternalId) {
    return `${base} (${childInternalId})`;
  }
  return base;
}

export function ChildLink(props: ChildLinkProps) {
  const { childId, search, className, "data-testid": dataTestId } = props;
  const label = formatChildLabel(props);
  if (!childId) {
    return (
      <EntityLinkPlain
        icon={User}
        title={label}
        className={className}
        data-testid={dataTestId}
      >
        {label}
      </EntityLinkPlain>
    );
  }
  return (
    <EntityLink
      to={`/children/${childId}/edit${search ? `?${search}` : ""}`}
      icon={User}
      title={label}
      className={className}
      data-testid={dataTestId}
    >
      {label}
    </EntityLink>
  );
}
